import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authStore, profileStore, formatApiErrorDetail } from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext(null);

function applyUserLang(data) {
  const lang = data && data.lang;
  if (lang && ["fr", "en", "es", "nl"].includes(lang) && (i18n.language || "").slice(0, 2) !== lang) {
    i18n.changeLanguage(lang);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = auth
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyUserLang(data);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    profileStore.clear();
    const { data } = await api.post("/auth/login", { email, password });
    authStore.set(data.access_token, data.refresh_token);
    setUser(data);
    applyUserLang(data);
    return data;
  };

  const register = async (name, email, password, ref, acceptedTerms) => {
    profileStore.clear();
    const lang = (i18n.language || "fr").slice(0, 2);
    const { data } = await api.post("/auth/register", { name, email, password, ref: ref || undefined, accepted_terms: !!acceptedTerms, lang });
    authStore.set(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-blocking: we clear the local session regardless of network outcome.
      console.debug("logout request failed (session cleared locally):", e?.message || e);
    }
    authStore.clear();
    profileStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout, refresh, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
